import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PARENT_SECONDARY_MAP } from '@/layouts/UniversalLayout/routes';

const STACK_KEY = 'filter_route_stack';
const MENU_NAV_KEY = 'filter_menu_nav';

/** 根据当前 pathname（列表页）返回其二级页面路径 */
function getSiblingRoutes(pathname: string): string[] {
  return PARENT_SECONDARY_MAP[pathname] || [];
}

/**
 * 二级页面 → 包含它的一级页面列表 的反向映射。
 * 用于支持一个二级页（如 /products/relateProduct）从多个一级页面跳转进入的场景。
 */
const SECONDARY_PARENTS_MAP: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {};
  for (const [primary, secondaries] of Object.entries(PARENT_SECONDARY_MAP)) {
    for (const sec of secondaries) {
      (map[sec] ||= []).push(primary);
    }
  }
  return map;
})();

/**
 * 反查当前路径对应的「一级菜单 key」。
 * 若当前是某一级列表页本身，或处于其二级页面（详情/编辑），
 * 均返回该一级列表页路径，使侧边栏高亮保持在该一级菜单项上。
 *
 * 对于归属多个一级页面的二级页（如 /products/relateProduct 同时出现在
 * collection / posts / products-list 下），根据路由访问历史栈中离当前页
 * 最近的一级来源页面来确定高亮，从而正确反映「从哪个一级页面跳转过来」。
 */
export function getPrimaryMenuKey(pathname: string): string | null {
  // 当前路径本身是某一级列表页
  if (PARENT_SECONDARY_MAP[pathname]) return pathname;

  // 找到所有把当前路径作为二级页的一级页面
  const candidates = SECONDARY_PARENTS_MAP[pathname];
  if (!candidates || candidates.length === 0) return null;

  // 仅归属一个一级页面，直接返回
  if (candidates.length === 1) return candidates[0];

  // 归属多个一级页面：依据路由历史栈确定真正的来源一级页面
  const stack = getRouteStack();
  for (let i = stack.length - 1; i >= 0; i--) {
    const prev = stack[i];
    if (prev === pathname) continue; // 跳过自身（F5 重载可能造成的重复项）
    if (candidates.includes(prev)) return prev;
  }

  // 找不到来源时，回退到第一个候选一级页面
  return candidates[0];
}

/** 读取路由访问历史栈（用于判断来源页面是否为二级页面） */
function getRouteStack(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(STACK_KEY) || '[]');
  } catch {
    return [];
  }
}

/** 记录一次路由访问（去重相邻重复），供全局使用，无需在每个二级页挂载 hook */
export function recordFilterRoute(pathname: string): void {
  const stack = getRouteStack();
  if (stack[stack.length - 1] !== pathname) {
    stack.push(pathname);
    if (stack.length > 50) stack.shift();
    sessionStorage.setItem(STACK_KEY, JSON.stringify(stack));
  }
}

export function useFilterGoPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // 路由历史由 UniversalLayout 全局记录（recordFilterRoute），
  // 这样所有二级页无需逐个挂载 hook 也能被历史栈追踪。

  /**
   * 列表页：保存当前筛选条件并跳转二级页面。
   * 条件以当前 pathname 为 key 存入 sessionStorage（一个页面一份）。
   * state 可选，透传给 navigate（二级页通过 location.state 接收参数时使用）。
   * highlightId 可选，跳转时记录需要高亮的行 id（返回列表页时由 consumeHighlightId 读取）。
   */
  const goPageWithSaveFilter = (
    path: string,
    filterState: { filterParams: any; pagination: any },
    state?: any,
    highlightId?: string | number
  ) => {
    sessionStorage.setItem(pathname, JSON.stringify(filterState));
    if (highlightId !== undefined && highlightId !== null && highlightId !== '') {
      sessionStorage.setItem(`${STACK_KEY}_highlight_${pathname}`, String(highlightId));
    }
    navigate(path, state ? { state } : undefined);
  };

  /** 判断「来源页面」是否为当前页面的二级页面 */
  const isFromSecondary = (): boolean => {
    const siblings = getSiblingRoutes(pathname);
    const temp = getRouteStack();
    // 去掉栈尾与当前页相同的项（处理 F5 重载造成的重复）
    while (temp.length && temp[temp.length - 1] === pathname) temp.pop();
    const prev = temp.length ? temp[temp.length - 1] : null;
    return !!prev && siblings.some((s) => prev === s || prev.startsWith(`${s}/`));
  };

  /**
   * 列表页挂载时调用：来源是二级页面则取出已保存条件（不删除，F5 仍保留）；
   * 否则返回 null（清除由 effect 处理）。
   * 注意：侧边栏点击切换进入时不恢复（视为全新进入）。
   */
  const getSavedFilter = () => {
    if (sessionStorage.getItem(MENU_NAV_KEY)) return null;
    if (!isFromSecondary()) return null;
    try {
      const raw = sessionStorage.getItem(pathname);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  // 非二级页面来源（如菜单全新进入 / 侧边栏切换）时清除已保存条件
  useEffect(() => {
    if (sessionStorage.getItem(MENU_NAV_KEY)) {
      // 侧边栏点击切换进入：清空该页已存条件及高亮标记
      sessionStorage.removeItem(pathname);
      sessionStorage.removeItem(`${STACK_KEY}_highlight_${pathname}`);
      sessionStorage.removeItem(MENU_NAV_KEY);
    } else if (!isFromSecondary()) {
      sessionStorage.removeItem(pathname);
      sessionStorage.removeItem(`${STACK_KEY}_highlight_${pathname}`);
    }
  }, [pathname]);

  /** 列表页主动清空（重置时调用） */
  const clearSavedFilter = () => {
    sessionStorage.removeItem(pathname);
    sessionStorage.removeItem(`${STACK_KEY}_highlight_${pathname}`);
  };

  /**
   * 列表页挂载时调用：从 sessionStorage 读取高亮行 id（key 按当前 pathname 隔离），
   * 读后立即删除，保证仅「从编辑/详情返回」那一次高亮；
   * 后续 fetchList（翻页/搜索/F5 刷新）不会再触发。无则返回空字符串。
   */
  const consumeHighlightId = (): string => {
    const key = `${STACK_KEY}_highlight_${pathname}`;
    const id = sessionStorage.getItem(key) || '';
    if (id) sessionStorage.removeItem(key);
    return id;
  };

  return { goPageWithSaveFilter, getSavedFilter, clearSavedFilter, consumeHighlightId };
}
